import * as XLSX from 'xlsx';
import { fetchFoodLogs, fetchWeightLogs } from '../services/clientApi';

const normalizeFoodItem = (item) => {
  if (!item) return null;
  if (item.macros && typeof item.macros === 'object') {
    return {
      name: item.name || 'Unknown Item',
      cals: item.macros.calories || 0,
      p: item.macros.protein_g || 0,
      c: item.macros.carbs_g || 0,
      f: item.macros.fat_g || 0,
    };
  }
  return {
    name: item.name || 'Unknown Item',
    cals: item.c || item.calories || 0,
    p: item.p || item.protein || 0,
    c: item.cb || item.carbs || 0,
    f: item.f || item.fat || 0,
  };
};

const parseFoodItems = (log) => {
  if (!log.food_items) return [];
  try {
    const foodItems = typeof log.food_items === 'string' ? JSON.parse(log.food_items) : log.food_items;
    if (Array.isArray(foodItems)) {
      return foodItems.map(normalizeFoodItem).filter(Boolean);
    }
  } catch (e) {
    console.error('Error parsing food_items:', e);
  }
  return [];
};

/**
 * @param {'1m'|'3m'|'6m'|'1y'|'custom'} preset
 * @param {string} [customStart] YYYY-MM-DD
 * @param {string} [customEnd] YYYY-MM-DD
 * @returns {{ start: Date, end: Date, startStr: string, endStr: string, labelKey: string } | { error: string }}
 */
export function getAnalyticsDateRange(preset, customStart, customEnd) {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const endStr = end.toISOString().split('T')[0];

  if (preset === 'custom') {
    if (!customStart || !customEnd) {
      return { error: 'missing_dates' };
    }
    const start = new Date(`${customStart}T00:00:00`);
    const endD = new Date(`${customEnd}T23:59:59.999`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(endD.getTime())) {
      return { error: 'invalid_dates' };
    }
    if (start > endD) {
      return { error: 'range_order' };
    }
    return {
      start,
      end: endD,
      startStr: customStart,
      endStr: customEnd,
      labelKey: 'custom',
    };
  }

  const start = new Date();
  start.setHours(0, 0, 0, 0);
  switch (preset) {
    case '1m':
      start.setMonth(start.getMonth() - 1);
      break;
    case '3m':
      start.setMonth(start.getMonth() - 3);
      break;
    case '6m':
      start.setMonth(start.getMonth() - 6);
      break;
    case '1y':
      start.setFullYear(start.getFullYear() - 1);
      break;
    default:
      start.setMonth(start.getMonth() - 1);
  }
  const startStr = start.toISOString().split('T')[0];
  return {
    start,
    end,
    startStr,
    endStr,
    labelKey: preset,
  };
}

function filterWeightLogsInRange(weightLogs, range) {
  return weightLogs.filter((log) => {
    const d = new Date(log.measurement_date);
    return !Number.isNaN(d.getTime()) && d >= range.start && d <= range.end;
  });
}

function filterFoodLogsInRange(foodLogs, range) {
  return foodLogs.filter((log) => {
    const dateStr = log.log_date || log.created_at?.split('T')[0];
    if (!dateStr) return false;
    const d = new Date(dateStr);
    return !Number.isNaN(d.getTime()) && d >= range.start && d <= range.end;
  });
}

function foodItemsSummaryText(items) {
  if (!items.length) return '';
  return items
    .map((it) => {
      const n = it.name || '';
      const c = Math.round(it.cals || 0);
      return c ? `${n} (${c} kcal)` : n;
    })
    .join('; ');
}

/**
 * @param {object} options
 * @param {string} options.userCode
 * @param {string} [options.clientName]
 * @param {'1m'|'3m'|'6m'|'1y'|'custom'} options.preset
 * @param {string} [options.customStart]
 * @param {string} [options.customEnd]
 * @param {'english'|'hebrew'} [options.language]
 */
export async function exportAnalyticsToExcel({
  userCode,
  clientName = '',
  preset,
  customStart,
  customEnd,
  exportType = 'all',
  language = 'english',
}) {
  const range = getAnalyticsDateRange(preset, customStart, customEnd);
  if (range.error) {
    return { ok: false, error: range.error };
  }

  const [{ data: weightLogsRaw, error: wErr }, { data: foodLogsRaw, error: fErr }] = await Promise.all([
    fetchWeightLogs(userCode),
    fetchFoodLogs(userCode, null, range.startStr, range.endStr),
  ]);

  if (wErr) console.error(wErr);
  if (fErr) console.error(fErr);

  const weightLogs = filterWeightLogsInRange(weightLogsRaw || [], range).sort(
    (a, b) => new Date(a.measurement_date) - new Date(b.measurement_date)
  );

  let foodLogs = foodLogsRaw || [];
  foodLogs = filterFoodLogsInRange(foodLogs, range);

  const weightRows = weightLogs.map((log) => ({
    Date: log.measurement_date,
    'Weight (kg)': log.weight_kg ?? '',
    'Body fat (%)': log.body_fat_percentage ?? '',
    'Waist (cm)': log.waist_circumference_cm ?? '',
    'Hip (cm)': log.hip_circumference_cm ?? '',
    'Arm (cm)': log.arm_circumference_cm ?? '',
    'Neck (cm)': log.neck_circumference_cm ?? '',
  }));

  const foodDetailRows = [];
  const sortedLogs = [...foodLogs].sort(
    (a, b) =>
      new Date(b.log_date || b.created_at) - new Date(a.log_date || a.created_at)
  );
  sortedLogs.forEach((log) => {
    const date = log.log_date || log.created_at?.split('T')[0] || '';
    const items = parseFoodItems(log);
    let cals = 0;
    let p = 0;
    let c = 0;
    let f = 0;
    items.forEach((it) => {
      cals += it.cals || 0;
      p += it.p || 0;
      c += it.c || 0;
      f += it.f || 0;
    });
    const hasFromItems = cals > 0 || p > 0 || c > 0 || f > 0;
    if (!hasFromItems) {
      cals = log.total_calories || 0;
      p = log.total_protein_g || 0;
      c = log.total_carbs_g || 0;
      f = log.total_fat_g || 0;
    }
    foodDetailRows.push({
      Date: date,
      Meal: log.meal_label || '',
      Calories: Math.round(cals),
      Protein_g: Math.round(p * 10) / 10,
      Carbs_g: Math.round(c * 10) / 10,
      Fat_g: Math.round(f * 10) / 10,
      Food_items: foodItemsSummaryText(items),
    });
  });

  const periodLabels = {
    english: {
      '1m': '1 month',
      '3m': '3 months',
      '6m': '6 months',
      '1y': '1 year',
      custom: 'Custom range',
    },
    hebrew: {
      '1m': 'חודש אחד',
      '3m': '3 חודשים',
      '6m': '6 חודשים',
      '1y': 'שנה',
      custom: 'טווח מותאם',
    },
  };
  const pl = periodLabels[language] || periodLabels.english;
  const periodDescription = pl[range.labelKey] || range.labelKey;
  const exportTypeLabel =
    exportType === 'body'
      ? (language === 'hebrew' ? 'גרף גוף' : 'Body graph')
      : exportType === 'food'
          ? (language === 'hebrew' ? 'יומן מזון' : 'Food logs')
          : (language === 'hebrew' ? 'הכל' : 'All');
  const exportNote =
    exportType === 'body'
      ? (language === 'hebrew' ? 'נתוני משקל/מדידות גוף (כמו בגרף הגוף).' : 'Weight/body metrics data (same as Body chart).')
      : exportType === 'food'
          ? (language === 'hebrew' ? 'שורות יומן מזון מפורטות.' : 'Detailed food log rows.')
          : (language === 'hebrew' ? 'כל הגיליונות.' : 'All sheets.');

  const summaryRows = [
    {
      Field: language === 'hebrew' ? 'לקוח' : 'Client',
      Value: clientName || userCode,
    },
    {
      Field: language === 'hebrew' ? 'קוד משתמש' : 'User code',
      Value: userCode,
    },
    {
      Field: language === 'hebrew' ? 'תקופה' : 'Period',
      Value: periodDescription,
    },
    {
      Field: language === 'hebrew' ? 'סוג ייצוא' : 'Export type',
      Value: exportTypeLabel,
    },
    {
      Field: language === 'hebrew' ? 'מתאריך' : 'From',
      Value: range.startStr,
    },
    {
      Field: language === 'hebrew' ? 'עד תאריך' : 'To',
      Value: range.endStr,
    },
    {
      Field: language === 'hebrew' ? 'ייצוא בתאריך' : 'Exported at',
      Value: new Date().toISOString(),
    },
    {
      Field: language === 'hebrew' ? 'הערות' : 'Notes',
      Value: exportNote,
    },
  ];

  const emptyMsg =
    language === 'hebrew' ? 'אין נתונים בתקופה שנבחרה' : 'No data in selected period';

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), 'Summary');
  if (exportType === 'body' || exportType === 'all') {
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        weightRows.length
          ? weightRows
          : [{ Date: emptyMsg, 'Weight (kg)': '', 'Body fat (%)': '', 'Waist (cm)': '', 'Hip (cm)': '', 'Arm (cm)': '', 'Neck (cm)': '' }]
      ),
      'Weight'
    );
  }
  if (exportType === 'food' || exportType === 'all') {
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        foodDetailRows.length
          ? foodDetailRows
          : [{ Date: emptyMsg, Meal: '', Calories: '', Protein_g: '', Carbs_g: '', Fat_g: '', Food_items: '' }]
      ),
      'Food logs'
    );
  }

  const safeCode = String(userCode).replace(/[^\w-]+/g, '_');
  const fileTypeSuffix =
    exportType === 'body'
      ? 'Body'
      : exportType === 'food'
          ? 'FoodLogs'
          : 'All';
  const filename = `Analytics_${fileTypeSuffix}_${safeCode}_${range.startStr}_${range.endStr}.xlsx`;
  XLSX.writeFile(wb, filename);

  return { ok: true, filename };
}
